import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';

// CSS for preview mode with affiliate product styles
const previewStyles = `
    /* Hide all chat widgets (Intercom, etc.) on preview page */
    .preview-page #intercom-container,
    .preview-page .intercom-launcher,
    .preview-page .intercom-lightweight-app,
    .preview-page [class*="intercom"],
    .preview-page iframe[name="intercom-launcher-frame"],
    .preview-page .crisp-client,
    .preview-page #hubspot-messages-iframe-container,
    .preview-page [class*="chat-widget"],
    .preview-page [class*="ChatWidget"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
    }

    /* Hide global chat widgets when preview page is active */
    body.preview-page-active #intercom-container,
    body.preview-page-active .intercom-launcher,
    body.preview-page-active .intercom-lightweight-app,
    body.preview-page-active iframe[name="intercom-launcher-frame"] {
        display: none !important;
        visibility: hidden !important;
    }

    /* Product card styles */
    .affiliate-product-card {
        background: #fff;
        border-radius: 16px;
        padding: 16px;
        margin-bottom: 16px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        border: 1px solid #f0f0f0;
    }
    .affiliate-product-card:hover {
        box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    }
    .product-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: #fff8e6;
        color: #b8860b;
        font-size: 11px;
        font-weight: 600;
        padding: 4px 10px;
        border-radius: 12px;
        margin-bottom: 12px;
    }
    .product-inner {
        display: flex;
        gap: 16px;
        align-items: flex-start;
    }
    .product-image {
        width: 100px;
        height: 100px;
        border-radius: 12px;
        object-fit: cover;
        flex-shrink: 0;
    }
    .product-image-placeholder {
        background: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .product-info {
        flex: 1;
        min-width: 0;
    }
    .product-title {
        font-size: 16px;
        font-weight: 600;
        color: #1a1a1a;
        margin: 0 0 4px 0;
    }
    .product-price {
        font-size: 14px;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0 0 4px 0;
    }
    .product-price .original {
        text-decoration: line-through;
        color: #999;
        font-weight: 400;
        margin-right: 6px;
    }
    .product-price .sale {
        color: #22c55e;
    }
    .product-rating {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 12px;
        color: #666;
        margin-bottom: 8px;
    }
    .product-rating .stars {
        color: #fbbf24;
    }
    .product-rating .count {
        color: #3b82f6;
    }
    .add-to-cart-btn {
        width: 100%;
        padding: 10px 20px;
        background: #fff;
        border: 1.5px solid #1a1a1a;
        border-radius: 24px;
        font-size: 13px;
        font-weight: 600;
        color: #1a1a1a;
        cursor: pointer;
        transition: all 0.2s;
        margin-top: 8px;
    }
    .add-to-cart-btn:hover {
        background: #1a1a1a;
        color: #fff;
    }
    .add-to-cart-btn.added {
        background: #22c55e;
        border-color: #22c55e;
        color: #fff;
    }

    /* Checkout bar styles */
    .checkout-bar {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #fff;
        border-top: 1px solid #eee;
        padding: 16px 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        box-shadow: 0 -4px 20px rgba(0,0,0,0.1);
        z-index: 1000;
        transform: translateY(100%);
        transition: transform 0.3s ease;
    }
    .checkout-bar.visible {
        transform: translateY(0);
    }
    .checkout-info {
        display: flex;
        align-items: center;
        gap: 12px;
    }
    .checkout-count {
        background: #1a1a1a;
        color: #fff;
        width: 32px;
        height: 32px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: 600;
    }
    .checkout-text {
        font-size: 14px;
        color: #666;
    }
    .checkout-text strong {
        color: #1a1a1a;
        font-weight: 600;
    }
    .checkout-btn {
        padding: 14px 32px;
        background: #1a1a1a;
        color: #fff;
        border: none;
        border-radius: 24px;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.2s;
    }
    .checkout-btn:hover {
        background: #333;
    }

    /* Section header */
    .section-header {
        text-align: center;
        margin-bottom: 16px;
    }
    .section-badge {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        color: #b8860b;
        font-size: 12px;
        font-weight: 600;
        margin-bottom: 6px;
    }
    .section-title {
        font-size: 16px;
        font-weight: 700;
        color: #1a1a1a;
        margin: 0;
    }

    /* Two column layout for desktop preview */
    .preview-two-column {
        display: grid;
        grid-template-columns: 200px 1fr;
        gap: 30px;
        align-items: start;
        width: 100%;
        max-width: 700px;
        padding: 30px;
    }
    .preview-sidebar {
        position: sticky;
        top: 30px;
    }
    .preview-content {
        max-width: 400px;
    }

    /* Mobile stacked layout */
    .preview-mobile-layout {
        display: flex;
        flex-direction: column;
        gap: 20px;
        width: 100%;
        padding: 0 16px;
    }
    .preview-mobile-layout .preview-sidebar {
        text-align: center;
    }
    .preview-mobile-layout .preview-sidebar .avatar-wrapper {
        justify-content: center;
    }
    .preview-mobile-layout .preview-sidebar .social-links {
        justify-content: center;
    }

    /* Social link hover effect */
    .social-link-item:hover {
        transform: scale(1.1);
        background-color: rgba(0,0,0,0.08) !important;
    }
`;

// Social Platforms Configuration
const SOCIAL_PLATFORMS = [
    {id: 'tiktok', name: 'TikTok', icon: 'M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z'},
    {id: 'youtube', name: 'YouTube', icon: 'M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z'},
    {id: 'twitter', name: 'X', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'},
    {id: 'instagram', name: 'Instagram', icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z'},
    {id: 'linkedin', name: 'LinkedIn', icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z'},
    {id: 'github', name: 'GitHub', icon: 'M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z'}
];

// Theme Presets
const THEMES = [
    {id: 'minimal', name: 'Minimal', bg: '#ffffff', text: '#000000'},
    {id: 'dark', name: 'Dark', bg: '#1a1a1a', text: '#ffffff'},
    {id: 'ocean', name: 'Ocean', bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#ffffff'},
    {id: 'sunset', name: 'Sunset', bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#ffffff'},
    {id: 'forest', name: 'Forest', bg: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', text: '#ffffff'},
    {id: 'purple', name: 'Purple', bg: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)', text: '#ffffff'},
    {id: 'custom', name: 'Custom', bg: '', text: '#ffffff'}
];

// Helper function to strip HTML tags and get plain text
function stripHtml(html) {
    if (!html) {return '';}
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

// Extract products from Lexical editor state
function extractProducts(editorContent) {
    const products = [];
    if (!editorContent || !editorContent.root || !editorContent.root.children) {
        return products;
    }

    const findProducts = (children) => {
        for (const child of children) {
            if (child.type === 'product') {
                const currency = child.productPriceCurrency || '$';
                const price = child.productPrice ? `${currency}${child.productPrice}` : '';
                const salePrice = child.productSalePrice ? `${currency}${child.productSalePrice}` : '';

                products.push({
                    id: child.productImageSrc || Math.random().toString(36),
                    title: stripHtml(child.productTitle) || 'Product',
                    description: stripHtml(child.productDescription) || '',
                    image: child.productImageSrc || '',
                    price: salePrice || price,
                    priceOriginal: salePrice ? price : '',
                    rating: child.productRatingEnabled ? (child.productStarRating || 4.5) : null,
                    ratingCount: child.productRatingEnabled ? Math.floor(Math.random() * 2000 + 100) : null,
                    buttonText: child.productButton || 'Add to Cart',
                    buttonUrl: child.productUrl || '#',
                    badge: child.productBadge || null
                });
            }
            if (child.children) {
                findProducts(child.children);
            }
        }
    };

    findProducts(editorContent.root.children);
    return products;
}

// Star rating component
function StarRating({rating}) {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
        <span className="stars">
            {'★'.repeat(fullStars)}
            {hasHalfStar && '★'}
            {'☆'.repeat(emptyStars)}
        </span>
    );
}

// Product Card Component
function ProductCard({product, onAddToCart, isInCart}) {
    const [imageError, setImageError] = React.useState(false);

    const hasValidImage = product.image &&
        product.image !== '<base64String>' &&
        !product.image.includes('<') &&
        !imageError;

    return (
        <div className="affiliate-product-card">
            {product.badge && (
                <div className="product-badge">
                    <span>★</span> {product.badge}
                </div>
            )}
            <div className="product-inner">
                {hasValidImage ? (
                    <img
                        src={product.image}
                        alt={product.title}
                        className="product-image"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="product-image product-image-placeholder">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                            <circle cx="8.5" cy="8.5" r="1.5"/>
                            <polyline points="21,15 16,10 5,21"/>
                        </svg>
                    </div>
                )}
                <div className="product-info">
                    <h3 className="product-title">{product.title}</h3>
                    {product.price && (
                        <p className="product-price">
                            {product.priceOriginal && (
                                <span className="original">{product.priceOriginal}</span>
                            )}
                            <span className={product.priceOriginal ? 'sale' : ''}>{product.price}</span>
                        </p>
                    )}
                    {product.rating && (
                        <div className="product-rating">
                            <span>{product.rating}</span>
                            <StarRating rating={product.rating} />
                            {product.ratingCount && (
                                <span className="count">({product.ratingCount.toLocaleString()})</span>
                            )}
                        </div>
                    )}
                    <button
                        className={`add-to-cart-btn ${isInCart ? 'added' : ''}`}
                        onClick={() => onAddToCart(product)}
                    >
                        {isInCart ? '✓ Added to Cart' : 'Add to Cart'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Checkout Bar Component
function CheckoutBar({cart, onCheckout}) {
    const totalItems = cart.length;
    const isVisible = totalItems > 0;

    const handleCheckout = () => {
        if (cart.length === 1) {
            window.open(cart[0].buttonUrl, '_blank');
        } else {
            window.open(cart[0].buttonUrl, '_blank');
        }
        onCheckout();
    };

    return (
        <div className={`checkout-bar ${isVisible ? 'visible' : ''}`}>
            <div className="checkout-info">
                <div className="checkout-count">{totalItems}</div>
                <div className="checkout-text">
                    <strong>{totalItems} item{totalItems !== 1 ? 's' : ''}</strong> in cart
                </div>
            </div>
            <button className="checkout-btn" onClick={handleCheckout}>
                Checkout
            </button>
        </div>
    );
}

// Mobile Device Frame Component
function MobileDeviceFrame({children, backgroundStyle}) {
    const [currentTime, setCurrentTime] = useState('');

    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            setCurrentTime(now.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit', hour12: true}));
        };
        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div style={deviceStyles.phoneContainer}>
            {/* Phone Frame */}
            <div style={deviceStyles.phoneFrame}>
                {/* Dynamic Island / Notch */}
                <div style={deviceStyles.dynamicIsland}>
                    <div style={deviceStyles.islandPill}></div>
                </div>

                {/* Status Bar */}
                <div style={deviceStyles.statusBar}>
                    <span style={deviceStyles.statusTime}>{currentTime}</span>
                    <div style={deviceStyles.statusIcons}>
                        {/* Signal */}
                        <svg width="17" height="12" viewBox="0 0 17 12" fill="none">
                            <rect x="0" y="8" width="3" height="4" rx="0.5" fill="#000"/>
                            <rect x="4.5" y="5" width="3" height="7" rx="0.5" fill="#000"/>
                            <rect x="9" y="2" width="3" height="10" rx="0.5" fill="#000"/>
                            <rect x="13.5" y="0" width="3" height="12" rx="0.5" fill="#000"/>
                        </svg>
                        {/* WiFi */}
                        <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                            <path d="M8 2.4C10.8 2.4 13.3 3.5 15.1 5.3L13.7 6.7C12.2 5.2 10.2 4.4 8 4.4C5.8 4.4 3.8 5.2 2.3 6.7L0.9 5.3C2.7 3.5 5.2 2.4 8 2.4Z" fill="#000"/>
                            <path d="M8 6.4C9.7 6.4 11.2 7.1 12.3 8.2L10.9 9.6C10.1 8.8 9.1 8.4 8 8.4C6.9 8.4 5.9 8.8 5.1 9.6L3.7 8.2C4.8 7.1 6.3 6.4 8 6.4Z" fill="#000"/>
                            <circle cx="8" cy="11" r="1.5" fill="#000"/>
                        </svg>
                        {/* Battery */}
                        <div style={deviceStyles.battery}>
                            <div style={deviceStyles.batteryBody}>
                                <div style={deviceStyles.batteryLevel}></div>
                            </div>
                            <div style={deviceStyles.batteryTip}></div>
                        </div>
                    </div>
                </div>

                {/* Screen Content */}
                <div style={{...deviceStyles.screenContent, background: backgroundStyle}}>
                    <div style={deviceStyles.scrollContainer}>
                        {children}
                    </div>
                </div>

                {/* Home Indicator */}
                <div style={deviceStyles.homeIndicator}></div>
            </div>
        </div>
    );
}

// Device frame styles
const deviceStyles = {
    phoneContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '20px'
    },
    phoneFrame: {
        width: '375px',
        height: '812px',
        backgroundColor: '#1a1a1a',
        borderRadius: '55px',
        padding: '12px',
        boxShadow: '0 50px 100px -20px rgba(0,0,0,0.4), 0 30px 60px -30px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.1)',
        position: 'relative',
        overflow: 'hidden'
    },
    dynamicIsland: {
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 20
    },
    islandPill: {
        width: '120px',
        height: '35px',
        backgroundColor: '#000',
        borderRadius: '20px'
    },
    statusBar: {
        position: 'absolute',
        top: '18px',
        left: '30px',
        right: '30px',
        height: '30px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 10,
        padding: '0 8px'
    },
    statusTime: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#000',
        fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
    },
    statusIcons: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px'
    },
    battery: {
        display: 'flex',
        alignItems: 'center'
    },
    batteryBody: {
        width: '25px',
        height: '12px',
        border: '1.5px solid #000',
        borderRadius: '3px',
        padding: '1.5px',
        position: 'relative'
    },
    batteryLevel: {
        width: '80%',
        height: '100%',
        backgroundColor: '#000',
        borderRadius: '1px'
    },
    batteryTip: {
        width: '2px',
        height: '5px',
        backgroundColor: '#000',
        borderRadius: '0 1px 1px 0',
        marginLeft: '1px'
    },
    screenContent: {
        width: '100%',
        height: '100%',
        borderRadius: '45px',
        overflow: 'hidden',
        position: 'relative'
    },
    scrollContainer: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingTop: '50px',
        paddingBottom: '30px',
        position: 'relative'
    },
    homeIndicator: {
        position: 'absolute',
        bottom: '18px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '135px',
        height: '5px',
        backgroundColor: '#000',
        borderRadius: '3px',
        zIndex: 10
    }
};

export default function DesignerPreview() {
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [editorContent, setEditorContent] = useState(null);
    const [viewMode, setViewMode] = useState('mobile');
    const [profileName, setProfileName] = useState('');
    const [cart, setCart] = useState([]);

    // Extract products from editor content
    const products = useMemo(() => {
        if (!editorContent) {return [];}
        return extractProducts(editorContent);
    }, [editorContent]);

    const handleAddToCart = (product) => {
        const isInCart = cart.some(item => item.id === product.id);
        if (isInCart) {
            setCart(cart.filter(item => item.id !== product.id));
        } else {
            setCart([...cart, product]);
        }
    };

    const handleCheckout = () => {
        setCart([]);
    };

    useEffect(() => {
        // Load active profile from the profiles array
        const activeProfileId = localStorage.getItem('designer-active-profile');
        const savedProfiles = localStorage.getItem('designer-profiles');

        if (savedProfiles) {
            try {
                const profiles = JSON.parse(savedProfiles);
                const activeProfile = profiles.find(p => p.id === activeProfileId) || profiles[0];

                if (activeProfile) {
                    setProfileName(activeProfile.name);
                    setSettings(activeProfile.settings || {});
                    setEditorContent(activeProfile.editorContent || null);
                }
            } catch (e) {
                console.error('Error loading profile:', e);
            }
        }
    }, []);

    // Hide external chat widget on preview page
    useEffect(() => {
        // Add class to body to help with global CSS targeting
        document.body.classList.add('preview-page-active');

        // Hide chat widgets directly
        const hideChatWidgets = () => {
            const selectors = [
                '#intercom-container',
                '.intercom-launcher',
                '.intercom-lightweight-app',
                'iframe[name="intercom-launcher-frame"]',
                '.crisp-client',
                '#hubspot-messages-iframe-container'
            ];
            selectors.forEach(selector => {
                const elements = document.querySelectorAll(selector);
                elements.forEach(el => {
                    el.style.setProperty('display', 'none', 'important');
                });
            });
        };

        hideChatWidgets();
        // Retry after delay in case widgets load late
        const timeout = setTimeout(hideChatWidgets, 1500);

        return () => {
            clearTimeout(timeout);
            document.body.classList.remove('preview-page-active');
            // Restore chat widgets
            const selectors = [
                '#intercom-container',
                '.intercom-launcher',
                '.intercom-lightweight-app',
                'iframe[name="intercom-launcher-frame"]',
                '.crisp-client',
                '#hubspot-messages-iframe-container'
            ];
            selectors.forEach(selector => {
                const elements = document.querySelectorAll(selector);
                elements.forEach(el => {
                    el.style.removeProperty('display');
                });
            });
        };
    }, []);

    if (!settings) {
        return (
            <div style={styles.loading}>
                <p>Loading preview...</p>
                <button onClick={() => navigate('/designer')} style={styles.backBtn}>
                    Back to Editor
                </button>
            </div>
        );
    }

    const currentTheme = THEMES.find(t => t.id === settings.theme) || THEMES[0];
    const backgroundStyle = settings.theme === 'custom' && settings.backgroundImage
        ? `url(${settings.backgroundImage}) center/cover`
        : currentTheme.bg;

    // Profile sidebar component
    const ProfileSidebar = () => (
        <div className="preview-sidebar">
            {/* Avatar */}
            <div className="avatar-wrapper" style={styles.avatarWrapper}>
                <div style={styles.avatar}>
                    {settings.profileImage ? (
                        <img src={settings.profileImage} alt={settings.displayName} style={styles.avatarImg} />
                    ) : (
                        <div style={styles.avatarPlaceholder}>
                            {settings.displayName?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                    )}
                </div>
            </div>

            {/* Name */}
            <h1 style={styles.displayName}>
                {settings.displayName || 'Your Name'}
            </h1>

            {/* Bio */}
            {settings.bio && (
                <p style={styles.bio}>
                    {settings.bio}
                </p>
            )}

            {/* Social Icons */}
            {settings.socials && Object.values(settings.socials).some(v => v) && (
                <div className="social-links" style={styles.socialLinks}>
                    {SOCIAL_PLATFORMS.filter(p => settings.socials[p.id]).map(platform => (
                        <a
                            key={platform.id}
                            href={settings.socials[platform.id]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-link-item"
                            style={styles.socialLink}
                            title={platform.name}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="#333">
                                <path d={platform.icon}/>
                            </svg>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );

    // Products content component
    const ProductsContent = () => (
        <div className="preview-content">
            {products.length > 0 && (
                <>
                    <div className="section-header">
                        {/* <div className="section-badge">
                            <span>★</span> BEST SELLER : {products.length * 100}+ SOLD!
                        </div> */}
                        <h2 className="section-title">MOST PURCHASE THIS WEEK</h2>
                    </div>

                    {products.map((product, index) => (
                        <ProductCard
                            key={product.id || index}
                            product={product}
                            onAddToCart={handleAddToCart}
                            isInCart={cart.some(item => item.id === product.id)}
                        />
                    ))}
                </>
            )}

            {products.length === 0 && (
                <p style={{textAlign: 'center', color: '#999', padding: '40px'}}>
                    No products added yet
                </p>
            )}
        </div>
    );

    // Mobile layout (stacked)
    const MobileProfileContent = () => (
        <div className="preview-mobile-layout">
            <ProfileSidebar />
            <ProductsContent />
        </div>
    );

    // Desktop layout (two columns)
    const DesktopProfileContent = () => (
        <div className="preview-two-column">
            <ProfileSidebar />
            <ProductsContent />
        </div>
    );

    return (
        <div className="designer-container preview-page" style={styles.container}>
            {/* Inject preview styles */}
            <style>{previewStyles}</style>

            {/* Top Toolbar */}
            <div style={styles.topToolbar}>
                {/* Back Button */}
                <button onClick={() => navigate('/designer')} style={styles.floatingBackBtn}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                    </svg>
                    Edit Profile
                </button>

                {/* View Toggle */}
                <div style={styles.viewToggle}>
                    <button
                        onClick={() => setViewMode('desktop')}
                        style={{
                            ...styles.viewBtn,
                            backgroundColor: viewMode === 'desktop' ? 'rgba(0, 0, 0, 0.1)' : 'transparent'
                        }}
                        title="Desktop View"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={viewMode === 'desktop' ? '#000000' : '#666'} strokeWidth="2">
                            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                            <line x1="8" y1="21" x2="16" y2="21"/>
                            <line x1="12" y1="17" x2="12" y2="21"/>
                        </svg>
                    </button>
                    <button
                        onClick={() => setViewMode('mobile')}
                        style={{
                            ...styles.viewBtn,
                            backgroundColor: viewMode === 'mobile' ? 'rgba(0, 0, 0, 0.1)' : 'transparent'
                        }}
                        title="Mobile View"
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={viewMode === 'mobile' ? '#000000' : '#666'} strokeWidth="2">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/>
                            <line x1="12" y1="18" x2="12.01" y2="18"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Content Area - Animated View Switching */}
            <div style={styles.viewContainer}>
                {/* Mobile Device Preview */}
                <div style={{
                    ...styles.mobileViewWrapper,
                    opacity: viewMode === 'mobile' ? 1 : 0,
                    transform: viewMode === 'mobile' ? 'scale(1) translateX(0)' : 'scale(0.9) translateX(-100px)',
                    pointerEvents: viewMode === 'mobile' ? 'auto' : 'none',
                    position: viewMode === 'mobile' ? 'relative' : 'absolute'
                }}>
                    <MobileDeviceFrame backgroundStyle={backgroundStyle}>
                        <div style={styles.mobileProfileWrapper}>
                            <MobileProfileContent />
                        </div>
                    </MobileDeviceFrame>
                </div>

                {/* Desktop Preview */}
                <div style={{
                    ...styles.desktopViewWrapper,
                    opacity: viewMode === 'desktop' ? 1 : 0,
                    transform: viewMode === 'desktop' ? 'scale(1) translateX(0)' : 'scale(0.9) translateX(100px)',
                    pointerEvents: viewMode === 'desktop' ? 'auto' : 'none',
                    position: viewMode === 'desktop' ? 'relative' : 'absolute'
                }}>
                    <div style={{...styles.desktopContainer, background: backgroundStyle}}>
                        <div style={styles.desktopProfileWrapper}>
                            <DesktopProfileContent />
                        </div>
                    </div>
                </div>
            </div>

            {/* Checkout Bar */}
            <CheckoutBar cart={cart} onCheckout={handleCheckout} />
        </div>
    );
}

const styles = {
    container: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '80px 20px 40px',
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)'
    },
    loading: {
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '20px',
        fontFamily: "'Inter', sans-serif"
    },
    backBtn: {
        padding: '10px 20px',
        background: '#000000',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        cursor: 'pointer',
        fontSize: '14px'
    },
    topToolbar: {
        position: 'fixed',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        zIndex: 100
    },
    floatingBackBtn: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 16px',
        background: 'rgba(255, 255, 255, 0.95)',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 500,
        color: '#333',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transition: 'transform 0.2s'
    },
    viewToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    },
    viewBtn: {
        padding: '8px',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent'
    },
    viewContainer: {
        position: 'relative',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100%',
        minHeight: '700px'
    },
    mobileViewWrapper: {
        transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    desktopViewWrapper: {
        width: '100%',
        maxWidth: '800px',
        transition: 'opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    },
    mobileProfileWrapper: {
        width: '100%',
        padding: '16px 0px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    desktopContainer: {
        width: '100%',
        maxWidth: '900px',
        minHeight: '600px',
        borderRadius: '16px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
        overflow: 'hidden',
        marginTop: '20px',
        background: '#f8f8f8'
    },
    desktopProfileWrapper: {
        width: '100%',
        padding: '40px 0px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    avatarWrapper: {
        display: 'flex',
        marginBottom: '16px'
    },
    avatar: {
        width: '100px',
        height: '100px',
        borderRadius: '50%',
        overflow: 'hidden',
        border: '3px solid #fff',
        boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
    },
    avatarImg: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
    },
    avatarPlaceholder: {
        width: '100%',
        height: '100%',
        backgroundColor: '#e8e8e8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '36px',
        fontWeight: '600',
        color: '#999'
    },
    displayName: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#1a1a1a',
        margin: '0 0 6px 0',
        letterSpacing: '-0.5px'
    },
    bio: {
        fontSize: '14px',
        color: '#666',
        margin: '0 0 16px 0',
        lineHeight: '1.5'
    },
    socialLinks: {
        display: 'flex',
        gap: '8px',
        flexWrap: 'wrap'
    },
    socialLink: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'transform 0.2s, background-color 0.2s',
        textDecoration: 'none',
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
    }
};

export {DesignerPreview};
